import {
  calcFromValues,
  computeFields,
  currencySign,
  CURRENCIES,
  findNorm,
  missingRates,
  needsForeignNorm,
  isForeignTrip,
  PAY_ADVANCE,
  PAY_EXPENSES,
  PAY_INVOICE,
  PAY_METHODS,
  applyPayments,
  expenseRows,
  RATE_FIXED_FIELD,
  SUBJECT_FIELD,
  syncSchedule,
  weekdayOf,
  type CalcSettings,
  type FieldDefinition,
  type RequestBehavior,
  type TableRow,
  type TaskSummaryDto,
  shownFields,
  TASK_STATUS_LABELS,
} from '@edms/shared';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useState, type InputHTMLAttributes } from 'react';
import { api } from '../lib/api';
import { useDepartmentsList, UserSelect, useUsersList } from '../lib/tasks-ui';

export type Values = Record<string, unknown>;

const money = new Intl.NumberFormat('ru-RU', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
export const fmtMoney = (v: unknown) => (typeof v === 'number' ? `${money.format(v)} ₽` : '—');
export const fmtDate = (v: unknown) => (typeof v === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(v) ? `${v.slice(8, 10)}.${v.slice(5, 7)}.${v.slice(0, 4)}` : '—');

function numberOrNull(s: string): number | null {
  if (s.trim() === '') return null;
  const n = Number(s.replace(/\s/g, '').replace(',', '.'));
  return Number.isFinite(n) ? n : null;
}

/** Оставляет то, из чего складывается число: цифры, пробелы, один разделитель, минус в начале. */
function cleanNumber(raw: string, decimals: number): string {
  let s = raw.replace(/[^\d\s.,-]/g, '');
  s = (s.trimStart().startsWith('-') ? '-' : '') + s.replace(/-/g, '');
  const sep = s.search(/[.,]/);
  if (sep >= 0) s = s.slice(0, sep + 1) + s.slice(sep + 1).replace(/[.,]/g, '').slice(0, decimals);
  // Больше 15 цифр число хранится неточно — лишние не вводятся (для номеров счетов есть вид «Номер»).
  let digits = 0;
  return [...s].filter((ch) => !/\d/.test(ch) || ++digits <= MAX_DIGITS).join('');
}

const MAX_DIGITS = 15;

/** Номер счёта, ИНН, БИК: только цифры, хранится строкой — без округления и с ведущими нулями. */
export function DigitsInput({ value, onChange, ...rest }: { value: unknown; onChange: (v: string | null) => void } & Omit<InputHTMLAttributes<HTMLInputElement>, 'value' | 'onChange'>) {
  return (
    <input
      className="input mono"
      inputMode="numeric"
      autoComplete="off"
      maxLength={50}
      {...rest}
      value={typeof value === 'string' ? value : typeof value === 'number' ? String(value) : ''}
      onChange={(e) => onChange(e.target.value.replace(/\D/g, '') || null)}
    />
  );
}

function formatNumber(v: unknown, decimals: number): string {
  return typeof v === 'number' ? v.toLocaleString('ru-RU', { maximumFractionDigits: decimals }) : '';
}

/**
 * Поле для числа и суммы: буквы и прочие символы не вводятся (и не вставляются),
 * запятая и точка — одно и то же, после ухода из поля число показывается с пробелами между разрядами.
 */
export function NumberInput({
  value,
  onChange,
  money,
  ...rest
}: { value: unknown; onChange: (v: number | null) => void; money?: boolean } & Omit<InputHTMLAttributes<HTMLInputElement>, 'value' | 'onChange'>) {
  const decimals = money ? 2 : 6;
  const [text, setText] = useState(() => formatNumber(value, decimals));
  // Значение поменялось снаружи (пересчёт, другой черновик) — показываем его.
  useEffect(() => {
    if (numberOrNull(text) !== (typeof value === 'number' ? value : null)) setText(formatNumber(value, decimals));
  }, [value]);
  return (
    <input
      className="input"
      inputMode="decimal"
      autoComplete="off"
      {...rest}
      value={text}
      onChange={(e) => {
        const next = cleanNumber(e.target.value, decimals);
        setText(next);
        onChange(numberOrNull(next));
      }}
      onBlur={(e) => {
        setText(formatNumber(numberOrNull(text), decimals));
        rest.onBlur?.(e);
      }}
    />
  );
}

/**
 * Поле «Связанный документ»: документы нужных видов (договоры для оплаты), где сотрудник участвовал;
 * галочка «показать все» — все документы этих видов в организации, кроме черновиков.
 */
function DocumentPicker({ field, value, onChange, taskId, requestTypeId, common }: {
  field: FieldDefinition;
  value: unknown;
  onChange: (v: unknown) => void;
  taskId?: string;
  requestTypeId?: string;
  common: { 'aria-invalid'?: boolean; id: string };
}) {
  const canShowAll = Boolean(requestTypeId && field.documentTypes?.length);
  const [all, setAll] = useState(false);
  const docs = useQuery({
    queryKey: ['tasks', 'linkable', requestTypeId, field.key, all],
    queryFn: () =>
      requestTypeId
        ? api.get<{ items: TaskSummaryDto[] }>('/tasks/linkable', { requestTypeId, field: field.key, all: String(all) })
        : api.get<{ items: TaskSummaryDto[] }>('/tasks', { scope: 'involved', kind: 'document', limit: 200 }),
    staleTime: 60_000,
  });
  const selected = typeof value === 'string' ? value : '';
  // Выбран документ, где сотрудник не участвовал, — сразу показываем все, иначе его не будет в списке.
  useEffect(() => {
    if (canShowAll && !all && selected && docs.data && !docs.data.items.some((d) => d.id === selected)) setAll(true);
  }, [canShowAll, all, selected, docs.data]);
  return (
    <span className="doc-picker">
      <select className="select" {...common} value={selected} onChange={(e) => onChange(e.target.value || null)}>
        <option value="">{docs.isPending ? 'Загрузка…' : docs.data?.items.length === 0 ? (all ? 'Документов нет' : 'Нет документов с вашим участием — отметьте «показать все»') : '—'}</option>
        {docs.data?.items
          .filter((d) => d.id !== taskId)
          .map((d) => (
            <option key={d.id} value={d.id}>
              №{d.number} · {TASK_STATUS_LABELS[d.status].toLowerCase()} · {d.title}
            </option>
          ))}
      </select>
      {canShowAll && (
        // Не <label>: поле целиком уже в <label> для списка, вложенная метка увела бы щелчок в список.
        <span className="check" title="Все документы этого вида в организации, кроме черновиков — не только те, где вы участвовали">
          <input type="checkbox" checked={all} onChange={(e) => setAll(e.target.checked)} aria-label="показать все" />
          <span
            onClick={(e) => {
              e.preventDefault();
              setAll((v) => !v);
            }}
          >
            показать все
          </span>
        </span>
      )}
    </span>
  );
}

export function FieldInput({
  field,
  value,
  onChange,
  invalid,
  taskId,
  requestTypeId,
}: {
  field: FieldDefinition;
  value: unknown;
  onChange: (v: unknown) => void;
  invalid?: boolean;
  taskId?: string;
  requestTypeId?: string;
}) {
  const departments = useDepartmentsList();
  const common = { 'aria-invalid': invalid, id: `f-${field.key}` };
  switch (field.type) {
    case 'textarea':
      return <textarea className="textarea" rows={3} {...common} value={typeof value === 'string' ? value : ''} onChange={(e) => onChange(e.target.value)} />;
    case 'digits':
      return <DigitsInput {...common} value={value} onChange={onChange} />;
    case 'number':
    case 'money':
      return <NumberInput {...common} value={value} onChange={onChange} money={field.type === 'money'} placeholder={field.type === 'money' ? '0,00 ₽' : ''} />;
    case 'date':
      return <input className="input" type="date" {...common} value={typeof value === 'string' ? value : ''} onChange={(e) => onChange(e.target.value || null)} />;
    case 'select':
      return (
        <select className="select" {...common} value={typeof value === 'string' ? value : ''} onChange={(e) => onChange(e.target.value || null)}>
          <option value="">—</option>
          {field.options?.map((o) => (
            <option key={o} value={o}>
              {o}
            </option>
          ))}
        </select>
      );
    case 'user':
      return <UserSelect id={common.id} value={typeof value === 'string' ? value : ''} onChange={(v) => onChange(v || null)} invalid={invalid} />;
    case 'department':
      return (
        <select className="select" {...common} value={typeof value === 'string' ? value : ''} onChange={(e) => onChange(e.target.value || null)}>
          <option value="">—</option>
          {departments.data?.map((d) => (
            <option key={d.id} value={d.id}>
              {d.name}
            </option>
          ))}
        </select>
      );
    case 'document':
      return <DocumentPicker field={field} value={value} onChange={onChange} taskId={taskId} requestTypeId={requestTypeId} common={common} />;
    case 'checkbox':
      return (
        <label className="check">
          <input type="checkbox" checked={value === true} onChange={(e) => onChange(e.target.checked)} />
          <span>Да</span>
        </label>
      );
    case 'table':
      return <TableInput field={field} rows={Array.isArray(value) ? (value as TableRow[]) : []} onChange={onChange} />;
    default:
      return <input className="input" {...common} value={typeof value === 'string' ? value : ''} onChange={(e) => onChange(e.target.value)} />;
  }
}

function TableInput({ field, rows, onChange }: { field: FieldDefinition; rows: TableRow[]; onChange: (v: TableRow[]) => void }) {
  const cols = field.columns ?? [];
  const set = (i: number, key: string, v: string | number | null) => onChange(rows.map((r, j) => (j === i ? { ...r, [key]: v } : r)));
  return (
    <div className="table-input">
      {rows.length > 0 && (
        <table className="table">
          <thead>
            <tr>
              {cols.map((c) => (
                <th key={c.key}>{c.label}</th>
              ))}
              <th />
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={i}>
                {cols.map((c) => (
                  <td key={c.key}>
                    {c.type === 'date' ? (
                      <input className="input" type="date" value={typeof r[c.key] === 'string' ? (r[c.key] as string) : ''} onChange={(e) => set(i, c.key, e.target.value || null)} aria-label={c.label} />
                    ) : c.type === 'digits' ? (
                      <DigitsInput value={r[c.key]} onChange={(v) => set(i, c.key, v)} aria-label={c.label} />
                    ) : c.type === 'text' ? (
                      <input className="input" value={typeof r[c.key] === 'string' ? (r[c.key] as string) : ''} onChange={(e) => set(i, c.key, e.target.value)} aria-label={c.label} />
                    ) : (
                      <NumberInput value={r[c.key]} onChange={(v) => set(i, c.key, v)} money={c.type === 'money'} aria-label={c.label} />
                    )}
                  </td>
                ))}
                <td>
                  <button type="button" className="btn btn-ghost btn-sm" onClick={() => onChange(rows.filter((_, j) => j !== i))} aria-label="Удалить строку">
                    ×
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      <button type="button" className="btn btn-ghost btn-sm" onClick={() => onChange([...rows, Object.fromEntries(cols.map((c) => [c.key, null]))])}>
        + строка
      </button>
    </div>
  );
}

/** Форма полей заявки. Ошибки приходят с сервера в виде «fields.<ключ>». */
export function FieldsForm({
  fields,
  values,
  onChange,
  errors = {},
  behavior,
  calc,
  taskId,
  requestTypeId,
}: {
  fields: FieldDefinition[];
  values: Values;
  onChange: (v: Values) => void;
  errors?: Record<string, string>;
  behavior: RequestBehavior;
  calc: CalcSettings | null;
  taskId?: string;
  /** Для «Связанного документа»: выбор из документов видов, заданных у поля. */
  requestTypeId?: string;
}) {
  const users = useUsersList();
  const set = (key: string, v: unknown) => {
    const next: Values = { ...values, [key]: v };
    if (behavior === 'trip') {
      // Даты поменялись — строки расписания по дням перестраиваются, написанное сохраняется.
      if (key === 'dateFrom' || key === 'dateTo') {
        const schedule = syncSchedule(next);
        if (schedule) next.schedule = schedule;
      }
      // Должность — из карточки выбранного сотрудника.
      if (key === SUBJECT_FIELD) next.position = users.data?.find((u) => u.id === v)?.position ?? null;
      // Суммы проезда, проживания и прочих — из строк «Расходы и оплата».
      if (key === 'payments') {
        onChange(applyPayments(next));
        return;
      }
    }
    onChange(next);
  };
  const layout = visibleFields(fields, values, behavior).map((f) => (
    <label key={f.key} className={`field${f.type === 'textarea' || f.type === 'table' ? ' wide' : ''}`} htmlFor={`f-${f.key}`} data-field={f.key}>
      <span>
        {f.label}
        {f.required && <span className="req"> *</span>}
      </span>
      {behavior === 'trip' && f.key === 'country' ? (
        <CountryInput value={values.country} onChange={(v) => set('country', v)} norms={calc?.norms ?? []} invalid={Boolean(errors['fields.country'])} />
      ) : behavior === 'trip' && f.key === 'position' && f.system ? (
        <PositionValue employeeId={values[SUBJECT_FIELD]} />
      ) : behavior === 'trip' && f.key === 'payments' && f.type === 'table' ? (
        <PaymentsInput values={values} calc={calc} onChange={(v) => set('payments', v)} onPerDiem={(v) => set('perDiemManual', v)} />
      ) : behavior === 'trip' && f.key === 'schedule' && f.type === 'table' ? (
        <ScheduleInput field={f} rows={Array.isArray(values.schedule) ? (values.schedule as TableRow[]) : []} onChange={(v) => set('schedule', v)} />
      ) : (
        <FieldInput field={f} value={values[f.key]} onChange={(v) => set(f.key, v)} invalid={Boolean(errors[`fields.${f.key}`])} taskId={taskId} requestTypeId={requestTypeId} />
      )}
      {f.hint && <span className="faint">{f.hint}</span>}
      {errors[`fields.${f.key}`] && <span className="field-error">{errors[`fields.${f.key}`]}</span>}
    </label>
  ));
  return (
    <div className="fields-grid">
      {layout}
      {behavior !== 'generic' && <CalcSummary behavior={behavior} values={values} calc={calc} />}
    </div>
  );
}

/**
 * «Расходы и оплата»: инициатор один раз вводит суммы проезда, проживания и прочих — строками,
 * у каждой строки свой способ оплаты (в подотчёт или по счёту). Суточные считаются сами
 * и всегда идут в подотчёт; их можно поправить вручную. Итог — ниже, в расчёте.
 */
function PaymentsInput({ values, calc, onChange, onPerDiem }: { values: Values; calc: CalcSettings | null; onChange: (rows: TableRow[]) => void; onPerDiem: (v: number | null) => void }) {
  const c = computeFields('trip', { ...values, perDiemManual: null, lodging: null }, calc ?? calcFromValues(values));
  // Заявка, заполненная до появления строк: суммы из прежних полей становятся строками «в подотчёт».
  const rows: TableRow[] = Array.isArray(values.payments)
    ? (values.payments as TableRow[])
    : PAY_EXPENSES.flatMap((e) => (typeof values[e.key] === 'number' && (values[e.key] as number) > 0 ? [{ expense: e.label, method: PAY_ADVANCE, amount: values[e.key] as number }] : []));
  const seeded = { ...values, payments: rows };

  return (
    <div className="pay-lines">
      <div className="pay-line">
        <div className="pay-head">
          <b>Суточные</b>
          <span className="faint">по расчёту {fmtMoney(c.perDiemCalc)}</span>
        </div>
        <div className="pay-part">
          <span className="pay-fixed">В подотчёт сотруднику</span>
          <NumberInput
            money
            placeholder={typeof c.perDiemCalc === 'number' ? `${c.perDiemCalc}` : 'Сумма, ₽'}
            value={values.perDiemManual}
            onChange={onPerDiem}
            aria-label="Суточные: сумма, если отличается от расчёта"
          />
        </div>
      </div>
      {PAY_EXPENSES.map((e) => {
        const mine = expenseRows(seeded, e.key);
        const lodgingNorm = e.key === 'lodging' && typeof c.lodgingCalc === 'number' ? c.lodgingCalc : null;
        const list: TableRow[] = mine.length ? mine : [{ expense: e.label, method: PAY_ADVANCE, amount: null }];
        const write = (next: TableRow[]) => onChange([...rows.filter((r) => r.expense !== e.label), ...next]);
        const patch = (i: number, p: TableRow) => write(list.map((r, j) => (j === i ? { ...r, ...p } : r)));
        const sum = list.reduce((s, r) => s + (typeof r.amount === 'number' ? r.amount : 0), 0);
        return (
          <div key={e.key} className="pay-line">
            <div className="pay-head">
              <b>{e.label}</b>
              {lodgingNorm !== null && <span className="faint">по норме {fmtMoney(lodgingNorm)}</span>}
              {list.length > 1 && <span>{fmtMoney(sum)}</span>}
            </div>
            {list.map((r, i) => (
              <div key={i} className="pay-part">
                <select className="select" value={r.method === PAY_INVOICE ? PAY_INVOICE : PAY_ADVANCE} onChange={(ev) => patch(i, { method: ev.target.value })} aria-label={`${e.label}: как оплачивается`}>
                  {PAY_METHODS.map((m) => (
                    <option key={m} value={m}>
                      {m === PAY_ADVANCE ? 'В подотчёт сотруднику' : 'По счёту организации'}
                    </option>
                  ))}
                </select>
                <NumberInput
                  money
                  placeholder={lodgingNorm !== null && list.length === 1 ? `${lodgingNorm} (по норме)` : 'Сумма, ₽'}
                  value={r.amount}
                  onChange={(v) => patch(i, { amount: v })}
                  aria-label={`${e.label}: сумма`}
                />
                {list.length > 1 && (
                  <button type="button" className="btn btn-ghost btn-sm" onClick={() => write(list.filter((_, j) => j !== i))} aria-label="Убрать строку">
                    ×
                  </button>
                )}
              </div>
            ))}
            <button
              type="button"
              className="btn btn-ghost btn-sm pay-split"
              onClick={() => write([...list, { expense: e.label, method: list[list.length - 1]?.method === PAY_INVOICE ? PAY_ADVANCE : PAY_INVOICE, amount: null }])}
            >
              + строка (разделить оплату)
            </button>
          </div>
        );
      })}
    </div>
  );
}

/** Должность из карточки сотрудника: вписать вручную нельзя. */
function PositionValue({ employeeId }: { employeeId: unknown }) {
  const users = useUsersList();
  const u = users.data?.find((x) => x.id === employeeId);
  if (!employeeId) return <div className="readonly-value faint">Выберите сотрудника</div>;
  if (users.isPending) return <div className="readonly-value faint">Загрузка…</div>;
  if (!u?.position) return <div className="readonly-value field-error">В карточке сотрудника не указана должность — её заполняет администратор в разделе «Сотрудники»</div>;
  return <div className="readonly-value">{u.position}</div>;
}

/** Расписание: строка на каждый день, дата и день недели — сами, пишется только план. */
function ScheduleInput({ field, rows, onChange }: { field: FieldDefinition; rows: TableRow[]; onChange: (v: TableRow[]) => void }) {
  if (rows.length === 0) return <div className="readonly-value faint">Укажите даты командировки — строки на каждый день появятся сами</div>;
  const other = (field.columns ?? []).filter((c) => c.key !== 'date' && c.key !== 'weekday');
  const set = (i: number, key: string, v: string) => onChange(rows.map((r, j) => (j === i ? { ...r, [key]: v } : r)));
  return (
    <div className="table-input">
      <table className="table">
        <thead>
          <tr>
            <th>Дата</th>
            <th>День недели</th>
            {other.map((c) => (
              <th key={c.key}>{c.label}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => {
            const date = typeof r.date === 'string' ? r.date : '';
            const weekend = r.weekday === 'суббота' || r.weekday === 'воскресенье';
            return (
              <tr key={date || i}>
                <td style={{ whiteSpace: 'nowrap' }}>{fmtDate(date)}</td>
                <td className={weekend ? 'weekend' : undefined}>{r.weekday ?? (date ? weekdayOf(date) : '')}</td>
                {other.map((c) => (
                  <td key={c.key}>
                    <input className="input" value={typeof r[c.key] === 'string' ? (r[c.key] as string) : ''} onChange={(e) => set(i, c.key, e.target.value)} aria-label={`${c.label}, ${fmtDate(date)}`} />
                  </td>
                ))}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

/** Скрытые условием поля и страна командировки (только для заграничной). */
function visibleFields(all: FieldDefinition[], values: Values, behavior: RequestBehavior): FieldDefinition[] {
  // Поля с условием «показывать, только если» — по текущим значениям формы.
  const fields = shownFields(all, values);
  if (behavior !== 'trip') return fields;
  // Суммы расходов вводятся в «Расходы и оплата» — отдельные поля сумм не показываем.
  const viaPayments = fields.some((f) => f.key === 'payments' && f.type === 'table');
  const hidden = new Set([...(isForeignTrip(values) ? [] : ['country']), ...(viaPayments ? ['travel', 'lodging', 'other', 'perDiemManual'] : [])]);
  return fields.filter((f) => !hidden.has(f.key));
}

/** Страна — из списка норм, с подсказками по мере ввода. */
function CountryInput({ value, onChange, norms, invalid }: { value: unknown; onChange: (v: string | null) => void; norms: CalcSettings['norms']; invalid?: boolean }) {
  const text = typeof value === 'string' ? value : '';
  const known = findNorm(norms, text);
  return (
    <>
      <input
        className="input"
        id="f-country"
        list="trip-countries"
        autoComplete="off"
        value={text}
        aria-invalid={invalid || (text !== '' && !known)}
        placeholder={norms.length ? 'Начните вводить название' : 'Нормы по странам не заданы в настройках'}
        onChange={(e) => onChange(e.target.value || null)}
      />
      <datalist id="trip-countries">
        {norms.map((n) => (
          <option key={n.country} value={n.country} />
        ))}
      </datalist>
      {text !== '' && !known && norms.length > 0 && <span className="field-error">Такой страны нет в нормах — выберите из списка</span>}
    </>
  );
}

const curFmt = new Intl.NumberFormat('ru-RU', { maximumFractionDigits: 2 });
const rateFmt = new Intl.NumberFormat('ru-RU', { minimumFractionDigits: 2, maximumFractionDigits: 4 });
const inCur = (v: number, code: string | null) => `${curFmt.format(v)} ${currencySign(code)}`;
const plural = (n: number, one: string, few: string, many: string) => {
  const m10 = n % 10;
  const m100 = n % 100;
  if (m10 === 1 && m100 !== 11) return one;
  if (m10 >= 2 && m10 <= 4 && (m100 < 12 || m100 > 14)) return few;
  return many;
};

/**
 * Формула суточных за границей с правилом дня пересечения границы:
 * день выезда — по норме страны, день возвращения — по российской ставке.
 */
function foreignPerDiemText(foreignDays: number | null, domesticDays: number | null, norm: number | null, code: string | null, rate: number | null, domesticRate: number | null): string {
  if (foreignDays === null || norm === null || rate === null) return '';
  if (foreignDays === 0.5) return ` = ½ нормы (выезд и возвращение в один день): 0,5 × ${inCur(norm, code)} = ${inCur(norm / 2, code)} × ${rateFmt.format(rate)} ₽`;
  const abroad = `${foreignDays} ${plural(foreignDays, 'день', 'дня', 'дней')} × ${inCur(norm, code)} = ${inCur(foreignDays * norm, code)} × ${rateFmt.format(rate)} ₽`;
  return domesticDays ? ` = ${abroad} + ${domesticDays} ${plural(domesticDays, 'день', 'дня', 'дней')} возвращения × ${fmtMoney(domesticRate)}` : ` = ${abroad}`;
}

/** Суточные за границей по единому тарифу: дни за границей × тариф + день возвращения по России. */
function flatPerDiemText(foreignDays: number | null, domesticDays: number | null, flat: number, domesticRate: number | null): string {
  if (foreignDays === null) return '';
  if (foreignDays === 0.5) return ` = ½ тарифа (выезд и возвращение в один день): 0,5 × ${fmtMoney(flat)}`;
  const abroad = `${foreignDays} ${plural(foreignDays, 'день', 'дня', 'дней')} × ${fmtMoney(flat)}`;
  return domesticDays ? ` = ${abroad} + ${domesticDays} ${plural(domesticDays, 'день', 'дня', 'дней')} возвращения × ${fmtMoney(domesticRate)}` : ` = ${abroad}`;
}

/** Расчёт на лету: то же, что посчитает сервер при сохранении. */
export function CalcSummary({ behavior, values, calc }: { behavior: RequestBehavior; values: Values; calc: CalcSettings | null }) {
  const c = computeFields(behavior, values, calc ?? calcFromValues(values));
  const n = (k: string) => (typeof c[k] === 'number' ? (c[k] as number) : null);
  const days = n('days');
  if (days === null) return <div className="calc wide faint">Укажите даты — система посчитает дни{behavior === 'trip' ? ' и смету' : ''}.</div>;
  const nights = n('nights') ?? 0;
  const foreign = behavior === 'trip' && isForeignTrip(values);
  const fixed = values[RATE_FIXED_FIELD] === true;
  const str = (k: string) => (typeof c[k] === 'string' ? (c[k] as string) : null);
  const pdNorm = n('perDiemNorm');
  const pdCur = str('perDiemCurrency');
  const pdRate = n('perDiemCurrencyRate');
  const lgNorm = n('lodgingNorm');
  const lgCur = str('lodgingCurrency');
  const lgRate = n('lodgingCurrencyRate');
  // Единый тариф для всех стран (настройка администратора) вместо норматива страны.
  const pdFlat = n('perDiemForeignFlat');
  const lgFlat = n('lodgingForeignFlat');
  const usedRates = [...new Map([pdCur && [pdCur, pdRate], lgCur && [lgCur, lgRate]].filter((x): x is [string, number | null] => Boolean(x))).entries()];
  const missing = foreign && calc ? missingRates(findNorm(calc.norms, values.country), calc.rates, calc) : [];
  const daysLabel = `${days} ${plural(days, 'день', 'дня', 'дней')}`;
  const nightsLabel = `${nights} ${plural(nights, 'ночь', 'ночи', 'ночей')}`;
  return (
    <div className="calc wide">
      <div>
        <b>{days}</b> дн.
        <span className="faint">
          {' '}
          · выходных {n('weekendDays')}
          {behavior === 'trip' ? ` · рабочих ${n('workDays')} · ночей ${nights}` : ''}
        </span>
      </div>
      {behavior === 'trip' && foreign && (
        <div className="calc-norms">
          <div>
            Нормы для страны <b>{typeof values.country === 'string' && values.country ? values.country : '—'}</b>: суточные{' '}
            {pdFlat !== null ? (
              <>
                <b>{fmtMoney(pdFlat)} в день</b>
                <span className="faint"> (единый тариф для всех стран)</span>
              </>
            ) : (
              <>
                <b>{pdNorm !== null ? `${inCur(pdNorm, pdCur)} в день` : 'не заданы'}</b>
                <span className="faint"> (ПП РФ от 26.12.2005 № 812, прил. 1)</span>
              </>
            )}
            , проживание{' '}
            {lgFlat !== null ? (
              <>
                <b>{fmtMoney(lgFlat)} в сутки</b>
                <span className="faint"> (единый тариф для всех стран)</span>
              </>
            ) : (
              <>
                <b>{lgNorm !== null ? `${inCur(lgNorm, lgCur)} в сутки` : 'не задано'}</b>
                <span className="faint"> (ПП РФ от 22.08.2020 № 1267)</span>
              </>
            )}
          </div>
          {(pdFlat === null || lgFlat === null) && (
          <div>
            Курс ЦБ РФ
            {typeof c.ratesDate === 'string' && <> на {fmtDate(c.ratesDate)}</>}:{' '}
            {usedRates.length === 0 ? (
              <b>—</b>
            ) : (
              usedRates.map(([code, r], i) => (
                <span key={code}>
                  {i > 0 && ', '}1 {currencySign(code)} = <b>{r !== null ? `${rateFmt.format(r)} ₽` : 'не задан'}</b>
                </span>
              ))
            )}
            <span className="faint">{fixed ? ' — зафиксирован при согласовании' : ' — предварительно; суммы пересчитаются по курсу на дату согласования'}</span>
          </div>
          )}
          <div className="faint">День выезда из России — по норме страны, день возвращения — по норме России (ПП РФ от 13.10.2008 № 749, п. 18–20)</div>
        </div>
      )}
      {behavior === 'trip' && (
        <>
          <div>
            Суточные: <b>{fmtMoney(c.perDiem)}</b>
            {typeof values.perDiemManual === 'number' && c.perDiemCalc !== c.perDiem && <span className="faint"> (по расчёту {fmtMoney(c.perDiemCalc)})</span>}
            <span className="faint">
              {foreign
                ? pdFlat !== null
                  ? flatPerDiemText(n('foreignDays'), n('domesticDays'), pdFlat, n('perDiemDomesticRate'))
                  : foreignPerDiemText(n('foreignDays'), n('domesticDays'), pdNorm, pdCur, pdRate, n('perDiemDomesticRate'))
                : ` = ${daysLabel} × ${fmtMoney(c.perDiemRate)}`}
            </span>
          </div>
          <div>
            Проживание: <b>{fmtMoney(c.lodgingTotal)}</b>
            {typeof values.lodging === 'number' && c.lodgingCalc !== c.lodgingTotal && <span className="faint"> (по норме {fmtMoney(c.lodgingCalc)})</span>}
            <span className="faint">
              {foreign
                ? lgFlat !== null
                  ? ` = ${nightsLabel} × ${fmtMoney(lgFlat)}`
                  : lgNorm !== null && lgRate !== null
                  ? ` = ${nightsLabel} × ${inCur(lgNorm, lgCur)} = ${inCur(nights * lgNorm, lgCur)} × ${rateFmt.format(lgRate)} ₽`
                  : ''
                : ` = ${nightsLabel} × ${fmtMoney(c.lodgingRate)}`}
            </span>
          </div>
          {typeof values.travel === 'number' && values.travel > 0 && <div>Проезд: <b>{fmtMoney(values.travel)}</b></div>}
          {typeof values.other === 'number' && values.other > 0 && <div>Прочие расходы: <b>{fmtMoney(values.other)}</b></div>}
          <div className="calc-total">Итого по смете: {fmtMoney(c.total)}</div>
          <div className="calc-pay">
            <span>
              В подотчёт сотруднику: <b>{fmtMoney(c.advanceTotal)}</b>
              <span className="faint"> (с суточными)</span>
            </span>
            <span>
              По счетам организации: <b>{fmtMoney(c.invoiceTotal)}</b>
            </span>
          </div>
          {foreign && calc && needsForeignNorm(calc) && !findNorm(calc.norms, values.country) && values.country ? (
            <div className="field-error">Для этой страны нет норм — суточные и проживание не посчитать</div>
          ) : null}
          {missing.length > 0 && (
            <div className="field-error">Не задан курс: {missing.map((m) => CURRENCIES[m]?.name ?? m).join(', ')} — администратор задаёт курсы в настройках</div>
          )}
          {(n('weekendDays') ?? 0) > 0 && !values.weekendPay && <div className="field-error">Командировка захватывает выходные — выберите, как оплачивается работа в эти дни</div>}
        </>
      )}
    </div>
  );
}

/** Просмотр значений заявки (когда править уже нельзя). */
export function FieldsView({ fields, values, labels, behavior }: { fields: FieldDefinition[]; values: Values; labels: Record<string, string>; behavior: RequestBehavior }) {
  const show = (f: FieldDefinition) => {
    const v = values[f.key];
    if (v === null || v === undefined || v === '') return <span className="faint">—</span>;
    switch (f.type) {
      case 'money':
        return fmtMoney(v);
      case 'number':
        return formatNumber(v, 6);
      case 'date':
        return fmtDate(v);
      case 'checkbox':
        return v ? 'Да' : 'Нет';
      case 'user':
      case 'department':
      case 'document':
        return labels[f.key] ?? '—';
      case 'table':
        return (
          <table className="table compact">
            <tbody>
              {(v as TableRow[]).map((r, i) => (
                <tr key={i}>
                  {(f.columns ?? []).map((c) => (
                    <td key={c.key}>{c.type === 'date' ? fmtDate(r[c.key]) : c.type === 'money' ? fmtMoney(r[c.key]) : (r[c.key] ?? '')}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        );
      default:
        return <span className="prose">{String(v)}</span>;
    }
  };
  return (
    <dl className="fields-view">
      {visibleFields(fields, values, behavior).map((f) => (
        <div key={f.key} className={f.type === 'table' || f.type === 'textarea' ? 'wide' : ''}>
          <dt>{f.label}</dt>
          <dd>{show(f)}</dd>
        </div>
      ))}
      {behavior !== 'generic' && <CalcSummary behavior={behavior} values={values} calc={null} />}
    </dl>
  );
}
